import React, { useEffect, useState } from "react";
import api from "@/services/api.js";
import toast from "react-hot-toast";
import { Loader2, UserPlus, Users, Clock, User } from "lucide-react";
import FriendList from "@/pages/friends/FriendList";
import PendingRequests from "./friends/PendingRequest";
import AddFriend from "./friends/AddFriend";
import { useMyContext } from "@/context/ContextApi";
import { useTranslation } from 'react-i18next';


const FriendsPage = () => {
  const { t } = useTranslation();
  const [friends, setFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("friends");
  const { stompClient } = useMyContext();

  const fetchFriends = async () => {
    try {
      const response = await api.get("/friendships/friends");
      setFriends(Array.isArray(response.data) ? response.data : []);
    } catch {
      toast.error(t('friends_errorFetching'));
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const response = await api.get("/friendships/pending");
      setPendingRequests(Array.isArray(response.data) ? response.data : []);
    } catch {
      toast.error(t('friends_errorFetchingPending'));
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([fetchFriends(), fetchPendingRequests()]);
      setLoading(false);
    };
    fetchAll();
  }, []);

// WebSocket subscription
  useEffect(() => {
    if (!stompClient) return;

    const subscription = stompClient.subscribe(
      "/user/queue/friend-status",
      (message) => {
        const { userId, online } = JSON.parse(message.body);
        setFriends((prev) =>
          prev.map((friend) =>
            friend.id === userId ? { ...friend, online } : friend
          )
        );
      }
    );

    const friendRequestSub = stompClient.subscribe(
        "/user/queue/friend-request",
        () => {
            fetchPendingRequests();
            fetchFriends();
        }
    );

    return () => {
      subscription.unsubscribe();
      friendRequestSub.unsubscribe();
    }
  }, [stompClient]);
  //END WS

  const tabs = [
    { key: "friends", label: t('friends_tabFriends'), icon: Users, count: friends.length },
    { key: "pending", label: t('friends_tabPending'), icon: Clock, count: pendingRequests.length },
    { key: "add", label: t('friends_tabAddFriend'), icon: UserPlus, count: null },
  ];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-3">
        <Loader2 size={28} className="animate-spin text-violet-400" />
        <span className="text-sm text-zinc-500">{t('friends_loadingMessage')}</span>
      </div>
    );
  }

  return (

    <div className="min-h-[calc(100vh-64px)] px-4 py-12">
      <div className="max-w-5xl mx-auto">

        {/* Page header */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-2">
            <User size={16} className="text-violet-400" />
            <span className="text-xs font-mono uppercase tracking-widest text-violet-400">{t('friends_subtitle')}</span>
          </div>
          <h1 className="text-3xl font-bold text-white">{t('friends_title')}</h1>
          <p className="mt-1 text-zinc-500 text-sm">{t('friends_description')}</p>
        </div>

        {/* Friends */}
        {/* <GameUserList /> */}
        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/8 mb-4">
          {tabs.map(({ key, label, icon: Icon, count }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px
              ${activeTab === key
                  ? "border-violet-500 text-violet-400"
                  : "border-transparent text-zinc-500 hover:text-zinc-300"
                }`}
            >
              <Icon size={15} />
              {label}
              {count !== null && count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full
                ${key === "pending"
                    ? "bg-violet-500/20 text-violet-400"
                    : "bg-zinc-700 text-zinc-400"
                  }`}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === "friends" && (
          <FriendList
            friends={friends}
            onRefresh={fetchFriends}
          />
        )}
        {activeTab === "pending" && (
          <PendingRequests
            requests={pendingRequests}
            onRefresh={() => {
              fetchFriends();
              fetchPendingRequests();
            }}
          />
        )}
        {activeTab === "add" && (
          <AddFriend onRequestSent={fetchPendingRequests} />
        )}

      </div>
    </div>
  );
};

export default FriendsPage;